import { useState, type CSSProperties } from 'react';
import { UMButton, UMFormGroup, UMFormStyles, UMLegendItem, UMR, UMSelect, UMSidePanel } from '../../../urbanmap/_components';
import {
  DEFAULT_DENSITY_LEGEND, DENSITY_UNIT, formatDensity, LOOPS, NO_VALUE, validateThresholds,
  type DensityColors, type DensityLegendSettings,
} from './densityLegend';
import { DensityColorPicker } from './DensityColorPicker';

const ERROR = 'var(--nj-semantic-color-text-danger-primary-default, #db3735)';
const INPUT: CSSProperties = {
  flex: 1, minWidth: 0, height: 32, boxSizing: 'border-box', padding: '1px 12px', borderRadius: 0,
  border: `1px solid ${UMR.borderInput}`, fontFamily: UMR.font, fontSize: 14, color: UMR.black, background: UMR.white,
};
const SIGN: CSSProperties = { width: 14, flexShrink: 0, textAlign: 'center', fontSize: 14 };

/** Local copy of the form so "Annuler" leaves the saved settings untouched (E5). */
export function DensityLegendPanel({ legend, onSave, onClose }: {
  legend: DensityLegendSettings; onSave: (next: DensityLegendSettings) => void; onClose: () => void;
}) {
  const [raw, setRaw] = useState(() => legend.thresholds.map(formatDensity));
  const [colors, setColors] = useState<DensityColors>(legend.colors);
  const check = validateThresholds(raw);
  const setThreshold = (i: number, value: string) => setRaw((prev) => prev.map((v, j) => (j === i ? value : v)));
  const setColor = (i: number, color: string) =>
    setColors((prev) => prev.map((c, j) => (j === i ? color : c)) as DensityColors);
  const reset = () => { setRaw(DEFAULT_DENSITY_LEGEND.thresholds.map(formatDensity)); setColors(DEFAULT_DENSITY_LEGEND.colors); };
  const save = () => { if (check.thresholds) { onSave({ thresholds: check.thresholds, colors }); onClose(); } };
  const classLabel = (i: number) => `Couleur de la classe ${i + 1}`;
  return <UMSidePanel title="Modifier les légendes" onClose={onClose}
    footer={<>
      <UMButton variant="secondary" onClick={onClose}>Annuler</UMButton>
      <UMButton disabled={!check.thresholds} onClick={save}>Valider</UMButton>
    </>}>
    <UMFormStyles />
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <UMFormGroup label="Type de légende">
        <UMSelect ariaLabel="Type de légende" value="density" onChange={() => undefined}
          options={[{ value: 'density', label: 'Densité linéaire' }]} />
      </UMFormGroup>
      <div style={{ fontSize: 12, lineHeight: '18px', color: UMR.textSecondary }}>
        Les valeurs sont exprimées en {DENSITY_UNIT}
      </div>
      <div role="group" aria-label="Seuils et couleurs" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {raw.map((value, i) => <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span aria-hidden="true" style={SIGN}>≤</span>
          <input className="um-input" inputMode="decimal" value={value} aria-label={`Seuil ${i + 1} (${DENSITY_UNIT})`}
            aria-invalid={check.invalid[i] || undefined} aria-describedby={check.error ? 'density-legend-error' : undefined}
            onChange={(e) => setThreshold(i, e.target.value)}
            style={{ ...INPUT, borderColor: check.invalid[i] ? ERROR : UMR.borderInput }} />
          <DensityColorPicker label={classLabel(i)} value={colors[i]} onChange={(c) => setColor(i, c)} />
        </div>)}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span aria-hidden="true" style={SIGN}>+</span>
          <input disabled value={raw[2]} aria-label={`Au-delà du seuil 3 (${DENSITY_UNIT})`}
            style={{ ...INPUT, background: UMR.hover, color: UMR.textDisabled, cursor: 'not-allowed' }} />
          <DensityColorPicker label={classLabel(3)} value={colors[3]} onChange={(c) => setColor(3, c)} />
        </div>
      </div>
      {check.error && <div id="density-legend-error" role="alert"
        style={{ fontSize: 12, lineHeight: '18px', color: ERROR }}>{check.error}</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5, paddingTop: 12, borderTop: `1px solid ${UMR.borderSubtle}` }}>
        <span style={{ fontSize: 12, lineHeight: '18px', color: UMR.textSecondary }}>États particuliers (non modifiables)</span>
        <UMLegendItem label={NO_VALUE.label} color={NO_VALUE.color} kind="line" />
        <UMLegendItem label={LOOPS.label} color={LOOPS.color} kind="line" />
      </div>
      <div><UMButton variant="subtle" onClick={reset}>Rétablir les valeurs par défaut</UMButton></div>
    </div>
  </UMSidePanel>;
}
